import { withErrorBoundary } from '@sentry/react'
import { Tooltip } from 'antd'
import moment from 'moment-timezone'
import { Fragment, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Link } from 'react-router-dom'

import PlaybookIconGrey from 'src/assets/Playbooks.svg'
import PlaybookIconRed from 'src/assets/Playbooks_Selected.svg'
import EntranceLogo from 'src/assets/entrance.svg'
import IncidentIconGrey from 'src/assets/incidents.svg'
import IncidentIconRed from 'src/assets/incidents_selected.svg'
import MonitorIconGrey from 'src/assets/monitor.svg'
import MonitorIconRed from 'src/assets/monitor_selected.svg'
import OrgLogo from 'src/assets/placeholder.png'
import ReportIconGrey from 'src/assets/reports.svg'
import ReportIconRed from 'src/assets/reports_selected.svg'
import OnMountTransition from 'src/components/common/OnMountTransition'
import VersionInfo from 'src/components/common/VersionInfo'
import ScreenContext from 'src/context/screenContext'
import SimulationCtx from 'src/context/simulationCtx'
import { APP_ENV } from 'src/env'
import routes from 'src/routes'
import { handlePromise } from 'src/utils/handlePromise'
import useSidebar from 'src/utils/hooks/use-sidebar'
import { useOrganization } from 'src/utils/hooks/useOrganization'
import { useOrnaBackendClient } from 'src/utils/hooks/useOrnaBackendClient'
import logger from 'src/utils/logger'

import SentryFallback from '../SentryFallback'
import SwitchOrganizationModal from '../SwitchOrganization'

const Sidebar = () => {
    const ornaBackendClient = useOrnaBackendClient()

    const { setSidebarCollapsed } = useSidebar()
    const {
        legalName,
        attachments: { iconLogo },
    } = useOrganization()

    const { isEnabled: isSimulationEnabled } = useContext(SimulationCtx)

    const { isSmallScreen } = useContext(ScreenContext)

    const [initialCheck, setInitialCheck] = useState(
        localStorage.getItem('userOrganizations') ? false : true
    )

    const [userOrgsCount, setUserOrgsCount] = useState(() => {
        return localStorage.getItem('userOrganizations') || 0
    })

    const [showSwitchOrgView, setShowSwitchOrgView] = useState(false)

    const closeSwitchOrgViewModal = () => {
        setShowSwitchOrgView(false)
    }

    useEffect(() => {
        if (initialCheck && (userOrgsCount === 0 || !userOrgsCount)) {
            async function recheckOrgs() {
                const [axiosError, { data: expressResponse } = {}] =
                    await handlePromise(
                        ornaBackendClient.get(`/authorize-user`, {
                            headers: {
                                'Content-Type': 'application/json',
                            },
                        })
                    )

                if (axiosError || !expressResponse) {
                    logger.error('no response from express /authorize-user')
                    setInitialCheck(false)
                    return
                } else {
                    const responseOrganizations = expressResponse.organizations
                    setUserOrgsCount(responseOrganizations.length)
                    setInitialCheck(false)
                    return
                }
            }
            recheckOrgs()
        }
    }, [initialCheck, ornaBackendClient, userOrgsCount])

    return (
        <Fragment>
            <OnMountTransition>
                <div
                    className="fixed top-0 left-0 z-20 flex h-[100vh] flex-col overflow-hidden overflow-x-auto pt-10"
                    style={{
                        zIndex: 999,
                        minWidth: isSmallScreen ? '200px' : '16%',
                        backgroundColor: '#242e42',
                    }}
                >
                    <SwitchOrganizationModal
                        showModal={
                            APP_ENV.REACT_APP_STAGE === 'cypress'
                                ? false
                                : showSwitchOrgView
                        }
                        closeModal={closeSwitchOrgViewModal}
                    />
                    <div
                        className={`mt-4 flex h-full flex-col overflow-y-auto ${isSimulationEnabled ? 'mt-16 2xl:mt-20' : 'mt-10'}`}
                    >
                        <div className="px-2">
                            <img
                                src={iconLogo ? iconLogo : OrgLogo}
                                alt="org logo"
                                className="rounded-50-percent mx-auto h-20 w-20 cursor-pointer 2xl:h-24 2xl:w-24"
                            />
                            <div className="mt-2 flex w-full items-center justify-center">
                                <Link
                                    to={isSmallScreen ? null : '/settings'}
                                    className="w-auto cursor-pointer truncate text-base text-neutral-gray-100 2xl:text-xl"
                                    style={{ maxWidth: '180px' }}
                                >
                                    {legalName || '-'}
                                </Link>
                                {userOrgsCount > 1 &&
                                APP_ENV.REACT_APP_STAGE !== 'cypress' ? (
                                    <Tooltip
                                        title="Switch organization"
                                        color="#242E42"
                                    >
                                        <a
                                            href=""
                                            className="ml-2 h-6 w-6 cursor-pointer"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                setShowSwitchOrgView(true)
                                            }}
                                        >
                                            <img
                                                className="h-6 w-6"
                                                src={EntranceLogo}
                                                alt="entrance logo"
                                            />
                                        </a>
                                    </Tooltip>
                                ) : null}
                            </div>
                        </div>
                        <div className="mt-7 w-full flex-grow 2xl:mt-9">
                            <Section
                                sectionLabel="Plan"
                                routeName="plan"
                                imageGrey={PlaybookIconGrey}
                                imageRed={PlaybookIconRed}
                                setSidebarCollapsed={setSidebarCollapsed}
                            />
                            <Section
                                sectionLabel="Practice"
                                routeName="practice"
                                imageGrey={ReportIconGrey}
                                imageRed={ReportIconRed}
                                setSidebarCollapsed={setSidebarCollapsed}
                            />
                            <Section
                                sectionLabel="Respond"
                                routeName="respond"
                                imageGrey={IncidentIconGrey}
                                imageRed={IncidentIconRed}
                                setSidebarCollapsed={setSidebarCollapsed}
                            />
                            <Section
                                sectionLabel="Monitor"
                                routeName="monitor"
                                imageGrey={MonitorIconGrey}
                                imageRed={MonitorIconRed}
                                setSidebarCollapsed={setSidebarCollapsed}
                            />
                        </div>
                        <div className="mt-auto mb-4 p-1">
                            <div className="pl-8">
                                <span className="text-xs text-neutral-gray-75">
                                    ©{moment().year()} ORNA Inc.
                                </span>
                                <br />
                                <span className="text-xs text-neutral-gray-75">
                                    All rights reserved.
                                </span>
                            </div>
                            <div className="mt-1 pl-8 text-xs italic text-neutral-gray-75">
                                <VersionInfo />
                            </div>
                        </div>
                    </div>
                </div>
            </OnMountTransition>
        </Fragment>
    )
}

const Section = ({
    routeName,
    imageGrey,
    imageRed,
    sectionLabel,
    isComingSoon = false,
    setSidebarCollapsed,
}) => {
    const location = useLocation()
    const { isSmallScreen } = useContext(ScreenContext)
    return (
        <Fragment>
            <Link to={routes[routeName]}>
                <div
                    className="flex w-full items-center py-1 2xl:py-2"
                    onClick={() => {
                        if (isSmallScreen) {
                            setSidebarCollapsed(true)
                        }
                    }}
                >
                    <div
                        className={
                            location.pathname.includes(routes[routeName])
                                ? `h-10 w-2 bg-primary-red-500 2xl:h-12`
                                : `h-10 w-2 bg-neutral-gray-500 2xl:h-12`
                        }
                    ></div>
                    <div
                        className={
                            location.pathname.includes(routes[routeName])
                                ? `flex h-10 w-full cursor-pointer items-center bg-neutral-gray-700 px-5 py-1 transition-all duration-500 ease-out xl:px-6 2xl:h-12`
                                : `flex h-10 w-full cursor-pointer items-center px-5 py-1 transition-all duration-500 ease-out hover:bg-neutral-gray-700 xl:px-6 2xl:h-12`
                        }
                    >
                        <div className="w-2/12">
                            <img
                                src={
                                    location.pathname.includes(
                                        routes[routeName]
                                    )
                                        ? imageRed
                                        : imageGrey
                                }
                                alt=""
                                className="h-5 w-5 2xl:h-6 2xl:w-6"
                            />
                        </div>
                        <div className="w-10/12 pl-6 text-sm text-neutral-gray-75 2xl:text-base">
                            {isComingSoon ? (
                                <Tooltip title="Coming Soon!" color="#242E42">
                                    <span className="">{sectionLabel}</span>
                                </Tooltip>
                            ) : (
                                <span className="">{sectionLabel}</span>
                            )}
                        </div>
                    </div>
                </div>
            </Link>
        </Fragment>
    )
}

export default withErrorBoundary(Sidebar, {
    fallback: <SentryFallback />,
})
